import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../axiosInstance';
import * as echarts from 'echarts';

const ChartComponent = () => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({
    xAxis: [],
    series: {
      Clients: [],
      Revenue: [],
      Expenses: []
    }
  });
  const [originalData, setOriginalData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [lastSavedData, setLastSavedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial data - NO CHANGES
  useEffect(() => {
    setIsLoading(true);
    axiosInstance.get('/api/data/vitals')
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const xAxis = res.data.map(entry => entry.month);
          const series = {
            Clients: res.data.map(entry => entry.Clients),
            Revenue: res.data.map(entry => entry.Revenue),
            Expenses: res.data.map(entry => entry.Expenses)
          };
          setChartData({ xAxis, series });
          setOriginalData(res.data); // Store original data with IDs
          setLastSavedData({ xAxis, series });
        } else {
          setChartData({
            xAxis: [],
            series: {
              Clients: [],
              Revenue: [],
              Expenses: []
            }
          });
          setOriginalData([]);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch chart data:', error);
        setIsLoading(false);
      });
  }, []);

  // Chart rendering and interaction - NO CHANGES
  useEffect(() => {
    if (!chartRef.current || chartData.xAxis.length === 0) return;
  
    const myChart = echarts.init(chartRef.current);
    myChart.clear();
  
    const option = {
      title: { text: 'Monthly Performance', left: 'center' },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          if (editingIndex !== null) {
            return `
              <div style="pointer-events: auto; padding: 10px">
                <h4>${chartData.xAxis[editingIndex]}</h4>
                <div style="margin: 5px 0">
                  <label>Clients:
                    <input type="number" id="edit-clients" value="${params[0].value}" step="any" min="0" style="width: 80px; margin-left: 5px; padding: 3px"/>
                  </label>
                </div>
                <div style="margin: 5px 0">
                  <label>Revenue:
                    <input type="number" id="edit-revenue" value="${params[1].value}" step="0.01" min="0" style="width: 80px; margin-left: 5px; padding: 3px"/>
                  </label>
                </div>
                <div style="margin: 5px 0">
                  <label>Expenses:
                    <input type="number" id="edit-expenses" value="${params[2].value}" step="0.01" min="0" style="width: 80px; margin-left: 5px; padding: 3px"/>
                  </label>
                </div>
              </div>
            `;
          }
          return `<b>${params[0].axisValue}</b><br/>` +
            params.map(p => `${p.seriesName}: ${p.value}`).join('<br/>');
        },
        alwaysShowContent: true,
        confine: true,
        position: (point, _, dom) =>
          editingIndex !== null ? [dom.offsetLeft, dom.offsetTop] : point
      },
      legend: { data: ['Clients', 'Revenue', 'Expenses'], bottom: 0 },
      grid: { containLabel: true, top: '15%', bottom: '15%' },
      xAxis: { type: 'category', data: chartData.xAxis },
      yAxis: { type: 'value' },
      series: Object.entries(chartData.series).map(([key, values]) => ({
        name: key,
        type: key === 'Revenue' ? 'line' : 'bar',
        data: values,
        smooth: key === 'Revenue',
        barWidth: '20%',
        itemStyle: key === 'Clients'
          ? { color: 'rgb(248, 163, 194)' }
          : key === 'Expenses'
          ? { color: '#c8bbbbc5' }
          : undefined
      }))
    };
  
    myChart.setOption(option);
  
    myChart.on('click', (params) => {
      if (params.componentType === 'series') {
        setEditingIndex(params.dataIndex);
        myChart.dispatchAction({
          type: 'showTip',
          seriesIndex: params.seriesIndex,
          dataIndex: params.dataIndex,
          position: [params.event.offsetX, params.event.offsetY]
        });
      }
    });
  
    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);
  
    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [chartData, editingIndex]);

  useEffect(() => {
    if (editingIndex === null) return;
  
    const handleClick = (event) => {
      if (!event.target || !event.target.__ecComponentInfo) {
        const clientsInput = document.getElementById('edit-clients');
        const revenueInput = document.getElementById('edit-revenue');
        const expensesInput = document.getElementById('edit-expenses');
  
        if (clientsInput && revenueInput && expensesInput) {
          const newClients = parseFloat(clientsInput.value);
          const newRevenue = parseFloat(revenueInput.value);
          const newExpenses = parseFloat(expensesInput.value);
  
          if (!isNaN(newClients) && !isNaN(newRevenue) && !isNaN(newExpenses)) {
            const updatedSeries = {
              Clients: [...chartData.series.Clients],
              Revenue: [...chartData.series.Revenue],
              Expenses: [...chartData.series.Expenses]
            };
  
            updatedSeries.Clients[editingIndex] = newClients;
            updatedSeries.Revenue[editingIndex] = newRevenue;
            updatedSeries.Expenses[editingIndex] = newExpenses;
  
            const isDataChanged = !lastSavedData || (
              JSON.stringify(updatedSeries) !== JSON.stringify(lastSavedData.series)
            );
  
            if (isDataChanged) {
              const newChartData = {
                xAxis: [...chartData.xAxis],
                series: updatedSeries
              };
              setChartData(newChartData);
  
              // CRITICAL FIX: Preserve original IDs and ensure no empty arrays
              const backendData = newChartData.xAxis.map((month, idx) => {
                // Find the corresponding original entry to preserve ID
                const originalEntry = originalData.find(item => item.month === month);
                return {
                  _id: originalEntry ? originalEntry._id : undefined,
                  month,
                  Clients: updatedSeries.Clients[idx],
                  Revenue: updatedSeries.Revenue[idx],
                  Expenses: updatedSeries.Expenses[idx]
                };
              });
  
              // GUARANTEE: Only send data if we have valid entries
              if (backendData.length > 0 && backendData.every(entry => 
                entry.month && 
                typeof entry.Clients === 'number' && 
                typeof entry.Revenue === 'number' && 
                typeof entry.Expenses === 'number'
              )) {
                axiosInstance.post('/api/data/vitals', backendData)
                  .then(() => setLastSavedData(newChartData))
                  .catch(error => {
                    console.error('Failed to save chart data:', error);
                    // Revert on error to prevent data loss
                    setChartData(lastSavedData);
                  });
              } else {
                console.warn('Invalid data detected, not sending to backend');
                setChartData(lastSavedData); // Revert to last good state
              }
            }
          }
        }
        setEditingIndex(null);
      }
    };
  
    const zr = echarts.getInstanceByDom(chartRef.current)?.getZr();
    zr?.on('click', handleClick);
  
    return () => {
      zr?.off('click', handleClick);
    };
  }, [editingIndex, chartData, lastSavedData, originalData]);
  
  if (isLoading) {
    return <div>Loading chart data...</div>;
  }

  if (chartData.xAxis.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No chart data available.</p>
        <p>Add data to your data.json file or through the API to see the chart.</p>
      </div>
    );
  }

  return (
    <div ref={chartRef} style={{ width: '80%', marginLeft: '-10px', height: '60%' }} />
  );
};

export default ChartComponent;